/**
 * Lightweight client-error reporter.
 *
 * Posts crash logs to the `client-errors-log` edge function which
 * inserts into `client_errors` for ops debug. Deliberately tiny:
 *  - no PII (email, tokens, names) ever included
 *  - no Sentry-sized dependency; one fetch, one fire-and-forget
 *  - silently no-ops in dev (DEV builds already get a console.error)
 *  - silently no-ops if Supabase env is missing (no spam in unit tests)
 *
 * The reporter is intentionally fault-tolerant: a network failure
 * while reporting must NEVER throw, because it's already running
 * inside an ErrorBoundary's componentDidCatch.
 */

interface ClientErrorPayload {
  message: string;
  name: string;
  stack?: string;
  route: string;
  componentStack?: string;
  userAgent: string;
  timestamp: string;
}

const ENDPOINT_PATH = "/functions/v1/client-errors-log";

export function reportClientError(payload: ClientErrorPayload): void {
  // Don't spam reports during dev; the console.error from the boundary
  // already surfaces it locally.
  if (import.meta.env.DEV) return;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as
    | string
    | undefined;
  if (!supabaseUrl || !supabaseKey) return;

  try {
    // Cap field sizes server-side too, but trim here so payload is small.
    const trimmed = {
      message: String(payload.message).slice(0, 500),
      name: String(payload.name).slice(0, 200),
      stack: payload.stack ? String(payload.stack).slice(0, 2000) : undefined,
      route: String(payload.route).slice(0, 200),
      component_stack: payload.componentStack
        ? String(payload.componentStack).slice(0, 2000)
        : undefined,
      user_agent: String(payload.userAgent).slice(0, 300),
      ts: payload.timestamp,
    };

    // Use keepalive so the report survives a page-unload after the
    // boundary triggers (e.g. user hits Refresh inside the fallback).
    void fetch(`${supabaseUrl}${ENDPOINT_PATH}`, {
      method: "POST",
      mode: "cors",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseKey,
      },
      body: JSON.stringify(trimmed),
    }).catch(() => {
      // Swallow: the reporter must never throw out of the boundary.
    });
  } catch {
    // Same: swallow synchronous JSON.stringify / URL errors silently.
  }
}
